/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { useRouterState } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import { forwardRef, useMemo } from 'react'

import { NavBadge } from '@/components/blocks/nav-badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

import { LABEL_COLLAPSED_TRANSITION, LABEL_EXPANDED_TRANSITION } from './side-nav-constants'
import { useSideNavContext } from './side-nav-context'

export interface SideNavMenuActionProps {
  icon: ReactElement
  label: string
  badge?: number | boolean
  isActive?: boolean | ((location: { pathname: string }) => boolean)
  children: ReactNode
  inactiveBgClassName?: string
  activeBgClassName?: string
  hoverBgClassName?: string
}

export const SideNavMenuAction = forwardRef<HTMLButtonElement, SideNavMenuActionProps>(
  function SideNavMenuAction(props, ref) {
    const {
      icon,
      label,
      badge,
      isActive: isActiveProp,
      children,
      inactiveBgClassName = 'bg-transparent',
      activeBgClassName = 'bg-muted',
      hoverBgClassName = 'hover:bg-card',
    } = props
    const { expanded, side } = useSideNavContext()
    const routerState = useRouterState()
    const currentPath = routerState.location.pathname

    const active = useMemo(() => {
      if (typeof isActiveProp === 'boolean') {
        return isActiveProp
      }
      if (typeof isActiveProp === 'function') {
        return isActiveProp({ pathname: currentPath })
      }
      return false
    }, [isActiveProp, currentPath])

    const colorClass = active ? 'text-primary' : 'text-muted-foreground'
    const hoverTextClass = active ? 'hover:text-primary' : 'hover:text-foreground'

    const menuSide = side === 'left' ? 'right' : 'left'
    const MenuDirectionIcon = side === 'left' ? ChevronRightIcon : ChevronLeftIcon

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <button
              className={cn(
                'relative mx-2 flex min-h-10 w-full cursor-pointer items-center gap-3 overflow-hidden rounded-md border-none bg-transparent px-3 py-[9px] text-left transition-colors',
                active ? activeBgClassName : inactiveBgClassName,
                hoverBgClassName,
                colorClass,
                hoverTextClass
              )}
              ref={ref}
              type="button"
            />
          }
        >
          <div className="relative flex shrink-0 items-center gap-2 [&_svg]:size-6">
            {expanded ? <span aria-hidden className="h-7 w-0.5 shrink-0 self-center rounded-sm" /> : null}
            <span className="inline-flex">{icon}</span>
            <NavBadge badge={badge} />
          </div>

          <div className="flex min-w-0 flex-1 justify-start overflow-hidden text-left">
            <div
              className="min-w-0 overflow-hidden"
              style={{
                maxWidth: expanded ? undefined : '0px',
                opacity: expanded ? 1 : 0,
                transition: expanded ? LABEL_EXPANDED_TRANSITION : LABEL_COLLAPSED_TRANSITION,
              }}
            >
              <span className={cn('block truncate text-lg md:text-base', active ? 'font-semibold' : 'font-medium')}>
                {label}
              </span>
            </div>
          </div>

          <MenuDirectionIcon aria-hidden className="size-4 shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side={menuSide} sideOffset={4}>
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)

SideNavMenuAction.displayName = 'SideNavMenuAction'
